// Value resolution and operator helpers for loop expressions.
// Example: `prefix-state` may subtract or hyphen-join here.
import { formatExpressionValue, resolveAddSubUnit, resolveDivisionUnit } from './placeholder-expression-format'
import { parseNumericValue, parseScopeValue } from './placeholder-expression-literals'
import { resolveVarExpressionValue } from './placeholder-expression-var'
import type { LoopExpressionToken, LoopExpressionValue } from './placeholder-expression-types'

// Resolve one primary token into a numeric or string expression value.
export function resolvePrimaryToken(
  token: LoopExpressionToken,
  scope: Record<string, string>,
  arrays: Record<string, string[]>,
  variables: Record<string, string>,
  objectVariables: Record<string, Record<string, string>>,
): LoopExpressionValue | null {
  // Check literal string tokens first.
  if (token.kind === 'string') {
    // Return the literal string token value.
    return { kind: 'string', value: token.value }
  }
  // Check literal numeric tokens next.
  if (token.kind === 'number') {
    // Return the parsed numeric literal token value.
    return parseNumericValue(token.value)
  }
  // Check compile-time `var(...)` calls next.
  if (token.kind === 'var-call') {
    // Return the resolved compile-time variable value.
    return resolveVarExpressionValue(token.value, scope, arrays, variables, objectVariables)
  }
  // Check identifier tokens against loop scope values.
  if (token.kind === 'identifier' && token.value in scope) {
    // Return the resolved identifier value from loop scope.
    return parseScopeValue(scope[token.value])
  }
  // Return null for unresolved primary token kinds.
  return null
}

// Apply one binary operator with numeric and string-aware semantics.
export function applyBinaryOperator(
  left: LoopExpressionValue,
  operator: 'plus' | 'minus' | 'star' | 'slash',
  right: LoopExpressionValue,
): LoopExpressionValue | null {
  // Check string-aware addition first.
  if (operator === 'plus') {
    // Return the addition result.
    return applyPlus(left, right)
  }
  // Check string-aware subtraction next.
  if (operator === 'minus') {
    // Return the subtraction result.
    return applyMinus(left, right)
  }
  // Check numeric-only operators after string cases.
  if (left.kind !== 'number' || right.kind !== 'number') {
    // Return null for non-numeric multiply or divide inputs.
    return null
  }
  // Check numeric multiplication before division.
  if (operator === 'star') {
    // Return the multiplication result.
    return applyMultiply(left, right)
  }
  // Check division-by-zero before unit resolution.
  if (right.value === 0) {
    // Return null for invalid zero division.
    return null
  }
  const unit = resolveDivisionUnit(left.unit, right.unit)
  // Stop when division cannot resolve a valid unit result.
  if (unit === null) {
    // Return null for incompatible division units.
    return null
  }
  // Return the numeric division result.
  return { kind: 'number', value: left.value / right.value, unit }
}
// Apply JS-like string-or-number addition.
function applyPlus(left: LoopExpressionValue, right: LoopExpressionValue): LoopExpressionValue | null {
  // Check whether both operands are numeric.
  if (left.kind === 'number' && right.kind === 'number') {
    const unit = resolveAddSubUnit(left.unit, right.unit)
    // Stop when additive units are incompatible.
    if (unit === null) {
      // Return null for incompatible numeric addition units.
      return null
    }
    // Return the numeric addition result.
    return { kind: 'number', value: left.value + right.value, unit }
  }
  // Return the concatenated string addition result.
  return { kind: 'string', value: `${formatExpressionValue(left)}${formatExpressionValue(right)}` }
}

// Apply numeric subtraction or hyphen-join fallback.
function applyMinus(left: LoopExpressionValue, right: LoopExpressionValue): LoopExpressionValue | null {
  // Check whether both operands are numeric.
  if (left.kind === 'number' && right.kind === 'number') {
    const unit = resolveAddSubUnit(left.unit, right.unit)
    // Stop when subtraction units are incompatible.
    if (unit === null) {
      // Return null for incompatible numeric subtraction units.
      return null
    }
    // Return the numeric subtraction result.
    return { kind: 'number', value: left.value - right.value, unit }
  }
  // Return the hyphen-joined string fallback result.
  return { kind: 'string', value: `${formatExpressionValue(left)}-${formatExpressionValue(right)}` }
}

// Apply numeric multiplication with CSS-like unit propagation.
function applyMultiply(left: LoopExpressionValue & { kind: 'number' }, right: LoopExpressionValue & { kind: 'number' }): LoopExpressionValue {
  const unit = !left.unit ? right.unit : !right.unit || left.unit === right.unit ? left.unit : ''
  // Return the numeric multiplication result.
  return { kind: 'number', value: left.value * right.value, unit }
}
